(function() {
  if (customElements.get('snkr-reviews-vice')) return;

  /*
   * Avis clients — défilement permanent.
   * Le marquee ne possède plus de bouton pause et ne s'arrête jamais de lui-même.
   * Il se contente de céder la main pendant qu'un visiteur fait défiler la piste au doigt
   * ou à la molette, puis reprend immédiatement après.
   */
  const KEYBOARD_SCROLL_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];

  class SnkrReviewsVice extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.scroller = this.querySelector('.snkr-reviews-vice__track');
      this.baseSpeed = parseFloat(this.dataset.scrollSpeed) || 3;
      this.speed = this.baseSpeed * 0.2;
      this.isInteracting = false;
      this.isEditorPaused = false;
      this.raf = null;
      this.wrapWidth = 0;
      this.exactScrollLeft = 0;
      this.wheelTimeout = null;
      this.touchTimeout = null;

      if (!this.scroller) return;

      this.cleanupClones();
      this.checkAndSetupScroll();

      this.resizeObserver = new ResizeObserver(() => {
        this.checkAndSetupScroll();
      });
      this.resizeObserver.observe(this);

      const signal = this.abortController.signal;

      // Éditeur de thème : on immobilise la piste le temps qu'un bloc est sélectionné,
      // puis le défilement reprend dès la désélection.
      document.addEventListener('shopify:block:select', (e) => {
        if (!this.contains(e.target)) return;
        this.isEditorPaused = true;
        const card = e.target.closest('.snkr-reviews-vice__card');
        if (card) card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }, { signal });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.contains(e.target)) this.isEditorPaused = false;
      }, { signal });

      this.scroller.addEventListener('touchstart', () => {
        clearTimeout(this.touchTimeout);
        this.isInteracting = true;
      }, { passive: true, signal });
      const endTouch = () => {
        clearTimeout(this.touchTimeout);
        this.touchTimeout = setTimeout(() => { this.isInteracting = false; }, 1200);
      };
      this.scroller.addEventListener('touchend', endTouch, { signal });
      this.scroller.addEventListener('touchcancel', endTouch, { signal });
      this.scroller.addEventListener('wheel', () => {
        this.isInteracting = true;
        clearTimeout(this.wheelTimeout);
        this.wheelTimeout = setTimeout(() => { this.isInteracting = false; }, 1000);
      }, { passive: true, signal });
      // Navigation clavier dans la piste (flèches, Début/Fin, Page) : courte trêve, puis
      // le défilement reprend tout seul. Le simple focus ne l'arrête jamais.
      this.scroller.addEventListener('keydown', (e) => {
        if (!KEYBOARD_SCROLL_KEYS.includes(e.key)) return;
        this.isInteracting = true;
        clearTimeout(this.keyTimeout);
        this.keyTimeout = setTimeout(() => { this.isInteracting = false; }, 2500);
      }, { signal });
    }

    cleanupClones() {
      const clones = this.scroller.querySelectorAll('.snkr-reviews-vice__clone');
      clones.forEach(clone => clone.remove());
    }

    checkAndSetupScroll() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.cleanupClones();
      this.wrapWidth = 0;

      const items = Array.from(this.scroller.children);
      if (items.length <= 1) return;

      let totalWidth = 0;
      items.forEach(item => totalWidth += item.offsetWidth);
      const trackStyle = getComputedStyle(this.scroller);
      const gap = parseFloat(trackStyle.gap) || 0;
      totalWidth += gap * (items.length - 1);
      totalWidth += (parseFloat(trackStyle.paddingLeft) || 0) + (parseFloat(trackStyle.paddingRight) || 0);

      // Pas de dépassement horizontal : rien à faire défiler.
      if (totalWidth <= this.scroller.clientWidth) return;

      items.forEach(item => {
        const clone = item.cloneNode(true);
        clone.classList.add('snkr-reviews-vice__clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.removeAttribute('id');

        const attributes = clone.attributes;
        for (let i = attributes.length - 1; i >= 0; i--) {
          const attr = attributes[i];
          if (attr.name.startsWith('data-shopify-')) {
            clone.removeAttribute(attr.name);
          }
        }

        const innerWithIds = clone.querySelectorAll('[id]');
        innerWithIds.forEach(el => el.removeAttribute('id'));

        this.scroller.appendChild(clone);
      });

      const originalFirst = items[0];
      const cloneFirst = this.scroller.querySelector('.snkr-reviews-vice__clone');

      if (cloneFirst && originalFirst) {
        this.wrapWidth = cloneFirst.offsetLeft - originalFirst.offsetLeft;
      }

      this.exactScrollLeft = this.scroller.scrollLeft;
      this.startAutoScroll();
    }

    startAutoScroll() {
      let lastTime = performance.now();

      const step = (time) => {
        if (!this.isConnected) return;
        const delta = Math.min(time - lastTime, 64);
        lastTime = time;

        const shouldYield = this.isEditorPaused || this.isInteracting;

        if (!shouldYield && this.wrapWidth > 0) {
          const pixelsToMove = this.speed * (delta / 16.66);
          this.exactScrollLeft += pixelsToMove;

          if (this.exactScrollLeft >= this.wrapWidth) {
            this.exactScrollLeft %= this.wrapWidth;
            this.scroller.scrollLeft = this.exactScrollLeft;
          } else {
            this.scroller.scrollLeft = this.exactScrollLeft;
            if (Math.abs(this.scroller.scrollLeft - this.exactScrollLeft) > 2) {
              this.exactScrollLeft = this.scroller.scrollLeft;
            }
          }
        } else {
          this.exactScrollLeft = this.scroller.scrollLeft;
          if (this.wrapWidth > 0 && this.scroller.scrollLeft >= this.wrapWidth) {
            this.scroller.scrollLeft -= this.wrapWidth;
            this.exactScrollLeft = this.scroller.scrollLeft;
          }
        }
        this.raf = requestAnimationFrame(step);
      };

      this.raf = requestAnimationFrame(step);
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      clearTimeout(this.wheelTimeout);
      clearTimeout(this.touchTimeout);
    }
  }

  customElements.define('snkr-reviews-vice', SnkrReviewsVice);
})();
